import {createRouter, createWebHistory} from "vue-router";
import NotFound from "./NotFound.vue";
import Login from "./Login.vue";
import Admin from "./Admin.vue";
import Landing from "./Landing.vue";
import Account from "./account/Account.vue";
import ResetTempPassword from "./ResetTempPassword.vue";
import ForgotPassword from "./ForgotPassword.vue";
import ResetPassword from "./ResetPassword.vue";
import ChangePassword from "./ChangePassword.vue";
import ResendWelcomeEmail from "./ResendWelcomeEmail.vue";
import twofa from "./twofa.vue";
import Application from "./admin/Applications/Application.vue";
import {inject} from "vue";
import {PortalAuthKey} from "./plugins/auth.ts";
import Register from "./Register.vue"

const routes = [
    {
        name: "app.notfound",
        path: "/:pathMatch(.*)*",
        component: NotFound,
    },
    {
        name: "app",
        path: "/",
        redirect: (_) => {
            return {name: "app.login"};
        },
    },
    {
        name: "app.register",
        path: "/register",
        component: Register
    },
    {
        name: "app.login",
        path: "/login",
        component: Login,
    },
    {
        name: "app.otp",
        path: "/otp",
        component: twofa,
    },
    {
        name: "app.temppass",
        path: "/temppass",
        component: ResetTempPassword,
    },
    {
        name: "app.forgotpass",
        path: "/forgotpass",
        component: ForgotPassword,
    },
    {
        name: "app.resetpass",
        path: "/resetpass",
        component: ResetPassword,
    },
    {
        name: "app.changepass",
        path: "/changepass",
        component: ChangePassword,
    },
    {
        name: "app.resendwelcome",
        path: "/resendwelcome",
        component: ResendWelcomeEmail,
    },
    {
        name: "app.landing",
        path: "/landing",
        component: Landing,
    },
    {
        name: "app.account",
        path: "/account",
        component: Account,
    },
    {
        name: "app.admin",
        path: "/admin",
        component: Admin,
        children: [
            {
                name: "app.admin.dashboard",
                path: "/admin/dashboard",
                component: () => import("./admin/Dashboard.vue"),
            },
            {
                name: "app.admin.sessions",
                path: "/admin/sessions",
                component: () => import("./admin/Sessions.vue"),
            },
            {
                name: "app.admin.apps",
                path: "/admin/apps",
                component: () => import("./admin/Applications.vue"),
                // children: [
                //   {
                //     name: "app.admin.apps.app.roles",
                //     path: "/admin/apps/:id/roles",
                //     component: () => import("./admin/Applications/Roles/Roles.vue"),
                //   },
                // ],
            },
            {
                name: "app.admin.apps.app",
                path: "/admin/apps/:appId",
                redirect: (_) => {
                    return {name: "app.admin.apps.app.overview"};
                },
                component: Application,
                children: [
                    {
                        name: "app.admin.apps.app.overview",
                        path: "/admin/apps/:appId/overview",
                        component: () =>
                            import("./admin/Applications/Overview/ApplicationOverview.vue"),
                    },
                    {
                        name: "app.admin.apps.app.users",
                        path: "/admin/apps/:appId/users",
                        component: () =>
                            import("./admin/Applications/Users/ApplicationUsers.vue"),
                    },
                    {
                        name: "app.admin.apps.app.callbacks",
                        path: "/admin/apps/:appId/callbacks",
                        component: () =>
                            import("./admin/Applications/Callbacks/ApplicationCallbacks.vue"),
                    },
                    {
                        name: "app.admin.apps.app.roles",
                        path: "/admin/apps/:appId/roles",
                        component: () => import("./admin/Applications/Roles/Roles.vue"),
                    },
                    {
                        name: "app.admin.apps.app.email-config",
                        path: "/admin/apps/:appId/email-config",
                        component: () => import("./admin/Applications/EmailConfig/ApplicationEmailConfig.vue"),
                    },
                ],
            },
            {
                name: "app.admin.urls",
                path: "/admin/urls",
                component: () => import("./admin/CallbackURLs.vue"),
            },
            {
                name: "app.admin.partners",
                path: "/admin/partners",
                component: () => import("./admin/Partners.vue"),
            },
            {
                name: "app.admin.users",
                path: "/admin/users",
                component: () => import("./admin/Users.vue"),
            },
            {
                name: "app.admin.permissions",
                path: "/admin/permissions",
                component: () => import("./admin/Permissions.vue"),
            },
            {
                name: "app.admin.whitelabel",
                path: "/admin/whitelabel",
                component: () => import("./admin/Whitelabel.vue"),
            },
            {
                name: "app.admin.settings",
                path: "/admin/settings",
                component: () => import("./admin/Settings/AdminSettings.vue")
            },
            {
                name: "app.admin.reports",
                path: "/admin/reports",
                component: () => import("./admin/Reports/AdminReports.vue")
            },
            {
                name: "app.admin.api-access",
                path: "/admin/api-access",
                component: () => import("./admin/ApiAccess.vue")
            },
        ],
    },
    // {
    //   path: "/:pathMatch(.*)*",
    //   name: "NotFound",
    //   component: NotFound,
    // },
];

const router = createRouter({
    //@ts-ignore
    routes: routes,
    history: createWebHistory(import.meta.env.BASE_URL),
});

router.beforeEach(async (to, _, next) => {
    const auth = inject(PortalAuthKey);

    if (!auth) {
        console.warn("[auth] Auth plugin not available");
        return next();
    }

    await auth.checkAuthStatus();

    // Only check auth status if unknown
    if (!auth.authState.isAuthenticated && !auth.authState.token) {
        await auth.checkAuthStatus();
    }

    const isAdmin = auth.authState.user?.isAdmin ?? false;

    // ✅ Let unauthenticated users access public routes
    const publicRoutes = ["app.login", "app.forgotpass", "app.resetpass", "app.resendwelcome", "app.register"];

    if (!auth.authState.isAuthenticated && publicRoutes.includes(to.name as string)){
        return next();
    }

    // ✅ Redirect away from login if already authenticated
    else if ((to.name === "app.login" || to.name === "app.register") && auth.authState.isAuthenticated && auth.authState.otpVerified) {
        return next({ name: "app.landing" });
    }

    // ❌ Not authenticated? Redirect to login page
    else if (!auth.authState.isAuthenticated && to.name !== "app.login") {
        return next({ name: "app.login" });
    }

    // ❌ Authenticated but OTP not verified? Force to OTP
    else if (to.name !== "app.otp" && to.path === "/otp" && auth.authState.isAuthenticated && !auth.authState.otpVerified) {
        return next({ name: "app.otp" });
    }

    // ❌ Trying to access admin area without admin rights?
    else if (to.fullPath.includes("/admin") && !isAdmin) {
        return next({ name: "app.landing" });
    }

    return next(); // ✅ All good
});



export default router;
